import React, { useCallback } from 'react';
import { Button, Checkbox, Typography, Table, Select, message } from 'antd';
import { Field, FieldArrayRenderProps, useFormikContext } from 'formik';
import { v4 as uuid } from 'uuid';

import { TableActions } from 'components';
import { getFileUploadSampleLink } from 'containers/BulkAction/components/BulkActionModal';
import { TRuleCreatePage } from 'containers/Rule/i18n/TModules';
import { useTenant } from 'hooks';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';
import { TGlobalListPage } from 'utils/globalTranslation/TModules';

const { Text } = Typography;

const ACCEPTED_FILE_TYPES = '.xlsx,.csv';
const ACCEPTED_FILE_PATTERN = /\.(xlsx|csv)$/i;

interface AudienceSetupProps extends FieldArrayRenderProps {}

const { Option } = Select;

const AudienceSetup = (props: AudienceSetupProps) => {
  const { push, remove } = props;
  const { values, setFieldValue } = useFormikContext<any>();
  const { name: tenantName } = useTenant();
  const { t } = useMultiLanguageTranslation();

  const handleFileUpload = useCallback((files: File[]) => {
    if (!files || files.length === 0) return;
    if (!ACCEPTED_FILE_PATTERN.test(files[0].name)) {
      message.error(t(`${TGlobalListPage}.InvalidCsvXlsx`));
      return false;
    }
    push({
      _id: uuid(),
      file: files[0],
      name: files[0].name,
      list_type: 'allow_list'
    });
    return true;
    // eslint-disable-next-line
  }, []);

  const tableColumns = [
    { title: 'Uploaded file', key: 'name', dataIndex: 'name', width: 320 },
    {
      title: 'Relation',
      key: 'relation',
      render: (_, record, index) => (
        <Field name={`user_groups.${index}.list_type`}>
          {({ field }) => (
            <Select
              value={field.value}
              onChange={value => setFieldValue(`user_groups.${index}.list_type`, value)}
              style={{ width: 160 }}
            >
              <Option value="allow_list">includes</Option>
              <Option value="deny_list">excludes</Option>
            </Select>
          )}
        </Field>
      )
    },
    {
      title: '',
      key: 'actions',
      width: 100,
      render: (_, record, index) => (
        <TableActions
          actions={[
            {
              title: 'Remove',
              onClick: () => remove(index)
            }
          ]}
        />
      )
    }
  ];

  return (
    <div>
      <Table
        columns={tableColumns}
        dataSource={values.user_groups}
        rowKey={group => group.id || group._id}
        footer={() => (
          <>
            <label htmlFor="add_upload">
              <Button
                size="large"
                onClick={() => document.getElementById('add_upload').click()}
                style={{ marginRight: 16 }}
              >
                Add Upload
              </Button>
            </label>
            <Text type="secondary">
              Accepted Format: .xlsx/.csv files only.{' '}
              <a href={getFileUploadSampleLink('upload_users', tenantName)}>Download sample</a>
            </Text>
            <input
              id="add_upload"
              name="add_upload"
              className="hidden"
              type="file"
              accept={ACCEPTED_FILE_TYPES}
              onChange={e => {
                e.preventDefault();
                const input = e.target;
                const files = Array.from(input.files);
                if (!handleFileUpload(files)) {
                  input.value = '';
                }
              }}
              data-testid="include-audience-file"
            />
            <div style={{ marginTop: 16 }}>
              <Checkbox
                checked={values.auto_create_customers ?? false}
                onChange={evt => setFieldValue('auto_create_customers', evt.target.checked)}
              >
                {t(`${TRuleCreatePage}.LoyaltyAudience.AudienceListSetup.AutoCreateCustomers`)}
              </Checkbox>
            </div>
          </>
        )}
      />
    </div>
  );
};

export default AudienceSetup;
